///
/// EarthBrowser
///
/// Created by Matt Giger on 8/25/2024
/// Copyright (c) 2024 Geodata Labs
///

import { path2d } from "@/earthbrowser/entities/TextNode/path2d"
import { type Font, type Path } from "@/earthbrowser/libs/opentype.js"
import { SweepContext } from "@/earthbrowser/libs/poly2tri"
import { logger } from "@/utils/LogDispatcher"
import { type color, white } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

interface glyph_segment {
	bounds: cube
	outline: vec2[]
	holes: vec2[][]
	char: string
	charIndex: number
}

export class TextGeometry implements Geometry, Material {
	public color: color = white
	public transparency: boolean = false
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public normal: VertexAttribute
	public texcoord: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	private curveResolution: number = 6

	constructor(context: StovokorContext, text: string, font: Font, size: number = 16, depthPercentage: number = 0.2) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.texcoord = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.texcoord])

		// convert each glyph to a path2d object
		const paths: Path[] = font.getPaths(text, 0, 0, size) as Path[]
		const glyphPaths: path2d[] = paths.map(path => {
			const glyphPath = new path2d()
			const cmds = path.commands as { type:string, x:number, y:number, x1:number, y1:number, x2:number, y2:number }[]
			for(const cmd of cmds) {
				switch(cmd.type) {
					case 'M':
						glyphPath.move(cmd.x, cmd.y)
						break
					case 'L':
						glyphPath.line(cmd.x, cmd.y)
						break
					case 'Q':
						glyphPath.quadratic(cmd.x1, cmd.y1, cmd.x, cmd.y)
						break
					case 'C':
						glyphPath.cubic(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y)
						break
					case 'Z':
						glyphPath.close()
						break
				}
			}
			return glyphPath
		})

		// transform the vertices into a set of polygons and holes
		const glyphSegments: glyph_segment[] = []
		glyphPaths.forEach((gpath: path2d, charIndex: number) => {
			const char = text[charIndex] || '?'
			const paths = gpath.extract(this.curveResolution)
			for(let i=0; i<paths.length; ++i) {
				const path = paths[i]
				const bounds = new cube()
				for(const v of path) {
					bounds.update(v.x, v.y)
				}
				if(i == 0) {
					glyphSegments.push({bounds: bounds, outline: path, holes: [], char, charIndex})
					continue
				}

				// see if this path is contained within an existing segment
				let found = false
				for(const segment of glyphSegments) {
					if(segment.bounds.contains(bounds)) {
						segment.holes.push(path)
						found = true
						break
					}
				}
				if(!found) {
					glyphSegments.push({bounds: bounds, outline: path, holes: [], char, charIndex})
				}
			}
		})

		// triangulate the vertices into polygon vertices and indices
		let index = 0
		glyphSegments.map((segment): void => {
			if(segment.outline.length == 0) {
				return
			}

			// Skip segments with too few vertices
			if(segment.outline.length < 3) {
				logger.warn(`TextGeometry: Skipping glyph '${segment.char}' (char ${segment.charIndex}) - segment has ${segment.outline.length} vertices (need at least 3)`)
				return
			}

			try {
				const ctx = new SweepContext([...segment.outline])	// Send a copy in, because poly2tri will modify the array
				// const ctx = new SweepContext(segment.outline, { cloneArrays: true })

				segment.holes.forEach(hole => ctx.addHole(hole))
				ctx.triangulate()

				// generate the front and back faces
				const zoffset = size * depthPercentage * 0.5
				const triangles = ctx.getTriangles()
				
				// Calculate UV bounds for this segment
				const minX = segment.bounds.min.x
				const minY = segment.bounds.min.y
				const maxX = segment.bounds.max.x
				const maxY = segment.bounds.max.y
				const uvWidth = maxX - minX
				const uvHeight = maxY - minY
				
				for(const t of triangles) {
					const points = t.getPoints()
					
			// Front face (z = -zoffset) - normal points toward viewer
			this.position.addPos(points[0].x, points[0].y, -zoffset)
			this.texcoord.add((points[0].x - minX) / uvWidth, (points[0].y - minY) / uvHeight)
			this.normal.add(0, 0, 1)
			this.position.addPos(points[1].x, points[1].y, -zoffset)
			this.texcoord.add((points[1].x - minX) / uvWidth, (points[1].y - minY) / uvHeight)
			this.normal.add(0, 0, 1)
			this.position.addPos(points[2].x, points[2].y, -zoffset)
			this.texcoord.add((points[2].x - minX) / uvWidth, (points[2].y - minY) / uvHeight)
			this.normal.add(0, 0, 1)
			this.indexBuffer.triangle(index, index+2, index+1)
			index += 3

			// Back face (z = +zoffset) - normal points away from viewer
			this.position.addPos(points[0].x, points[0].y, zoffset)
			this.texcoord.add((points[0].x - minX) / uvWidth, (points[0].y - minY) / uvHeight)
			this.normal.add(0, 0, -1)
			this.position.addPos(points[1].x, points[1].y, zoffset)
			this.texcoord.add((points[1].x - minX) / uvWidth, (points[1].y - minY) / uvHeight)
			this.normal.add(0, 0, -1)
			this.position.addPos(points[2].x, points[2].y, zoffset)
			this.texcoord.add((points[2].x - minX) / uvWidth, (points[2].y - minY) / uvHeight)
			this.normal.add(0, 0, -1)
			this.indexBuffer.triangle(index, index+1, index+2)
			index += 3
				}

				// generate the panels between the front and back faces
				// Process outline (outer edge - normals point outward)
				this.generateSidePanels(segment.outline, zoffset, index, false)
				index += segment.outline.length * 4
				
				// Process holes (inner edges - normals point inward)
				for(const hole of segment.holes) {
					this.generateSidePanels(hole, zoffset, index, true)
					index += hole.length * 4
				}
			} catch(error) {
				// Log triangulation errors and skip problematic segments
				const charCode = segment.char.charCodeAt(0)
				logger.warn(`TextGeometry: Failed to triangulate glyph '${segment.char}' (U+${charCode.toString(16).toUpperCase().padStart(4, '0')}, char ${segment.charIndex} in "${text}") - ${segment.outline.length} vertices, ${segment.holes.length} holes`, error)
			}
		})

		this.bounds = this.position.bounds
		const center = this.bounds.centroid
		this.bounds = flipAxesAroundCenter(center.x, center.y, this.position.values)
		this.boundingSphere = this.bounds.sphere
	}

	private generateSidePanels(vertices: vec2[], zoffset: number, startIndex: number, isHole: boolean): void {
		let edgeDistance = 0
		
		for(let i = 0; i < vertices.length; ++i) {
			const v1 = vertices[i]
			const v2 = vertices[(i + 1) % vertices.length]
			
		// Calculate edge direction and perpendicular normal
		const dx = v2.x - v1.x
		const dy = v2.y - v1.y
		const edgeLength = Math.sqrt(dx * dx + dy * dy)
		
		// Perpendicular vector (rotate 90 degrees counterclockwise in XY plane)
		// For CCW outlines, this points outward
		let nx = -dy / edgeLength
		let ny = dx / edgeLength
		
		// Flip normal for holes (inner edges should point inward to solid)
		if(isHole) {
			nx = -nx
			ny = -ny
		}
			
			// UV coordinates: U based on distance along edges, V based on depth (0 = front, 1 = back)
			const u1 = edgeDistance
			const u2 = edgeDistance + edgeLength
			edgeDistance = u2
			
			// Create quad for this edge segment
			// Front-left vertex (v1, front face)
			this.position.addPos(v1.x, v1.y, -zoffset)
			this.texcoord.add(u1, 0)
			this.normal.add(nx, ny, 0)
			
			// Front-right vertex (v2, front face)
			this.position.addPos(v2.x, v2.y, -zoffset)
			this.texcoord.add(u2, 0)
			this.normal.add(nx, ny, 0)
			
			// Back-left vertex (v1, back face)
			this.position.addPos(v1.x, v1.y, zoffset)
			this.texcoord.add(u1, 1)
			this.normal.add(nx, ny, 0)
			
			// Back-right vertex (v2, back face)
			this.position.addPos(v2.x, v2.y, zoffset)
			this.texcoord.add(u2, 1)
			this.normal.add(nx, ny, 0)
			
		// Two triangles to form the quad (CCW winding when viewed from outside)
		// Quad layout: idx+0=v1_front, idx+1=v2_front, idx+2=v1_back, idx+3=v2_back
		const idx = startIndex + i * 4
		this.indexBuffer.triangle(idx, idx + 2, idx + 1)
		this.indexBuffer.triangle(idx + 1, idx + 2, idx + 3)
		}
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

function flipAxesAroundCenter(cx: number, cy: number, vertices: number[]): cube {
	logger.assert(vertices.length % 3 == 0, "Vertex attribute must be 3D")

	const newBounds = new cube()
	const vlen = vertices.length
	for(let i=0; i<vlen; i+=3) {
		vertices[i] = vertices[i] - cx
		vertices[i+1] = vertices[i+1] - cy
		newBounds.update(vertices[i], vertices[i+1], vertices[i+2])
	}
	return newBounds
}